import "./styles/index.scss";
import "ca-online-design-system/dist/preflight.css";
import "ca-online-design-system/dist/style.css";
import "./styles/tailwind.css";

import { createApp } from "vue";
import skeleton from "./directives/skeleton";
import test from "./components/test.vue";
import footer from "./components/composed/footer/CaFooter.vue";
import header from "./components/composed/header/CaHeader.vue";
import pesquisaResultados from "./components/composed/pesquisa/CaPesquisaResultados.vue";
import pesquisaGrid from "./components/composed/pesquisa/CaPesquisaGrid.vue";
import appShowcaseModule from "./components/composed/AppShowcaseModule/AppShowcaseModule.vue";
import sliderCatalog from "./components/composed/slider-catalog/CaSliderCatalogModule.vue";
import alertBanner from "./components/composed/AlertBanner/AlertBanner.vue";
import banner from "./components/composed/banner/CaBanner.vue";
import promoModule from "./components/composed/promo/CaPromoModule.vue";
import quickActionCards from "./components/composed/quickActionCard/CaQuickActionCards.vue";
import newsModule from "./components/composed/noticias/CaNoticias.vue";
import highlightsSection from "./components/composed/highlightSection/CaHighlightSection.vue";
import bannerCarouselModule from "./components/composed/BannerCarouselModule/BannerCarouselModule.vue";
import arcFocusCarousel from "./components/composed/ArcFocusCarousel/ArcFocusCarousel.vue";
import eventsCalendar from "./components/composed/eventsCalendar/CaEventsCalendar.vue";
import overlappingCardsCarousel from "./components/composed/OverlappingCardsCaroussel/CaOverlappingCardsCarousel.vue";
import cardFanCarousel from "./components/composed/cardFanCarousel/CaCardFanCarousel.vue";
import testimonialMasonry from "./components/composed/testimonialMasonry/CaTestimonialMasonry.vue";
import actionsModule from "./components/composed/actionsModule/CaActionsModule.vue";
import keyInformation from "./components/composed/keyInformation/CaKeyInformation.vue";
import ctaHero from "./components/composed/cta-hero/CaCtaHero.vue";

const components = {
  appShowcaseModule,
  alertBanner,
  banner,
  bannerCarouselModule,
  header,
  test,
  footer,
  pesquisaResultados,
  pesquisaGrid,
  sliderCatalog,
  promoModule,
  quickActionCards,
  newsModule,
  highlightsSection,
  arcFocusCarousel,
  eventsCalendar,
  overlappingCardsCarousel,
  cardFanCarousel,
  ctaHero,
  testimonialMasonry,
  actionsModule,
  keyInformation,
};

/** Track mounted instances so they can be cleaned up if needed. */
const instances = new Map();
const revealExcludedComponents = new Set([
  "alertBanner",
  "banner",
  "footer",
  "header",
]);
let revealObserver;
const revealFinishHandlers = new Map();

const revealClasses = [
  "ca-mount-reveal",
  "is-reveal-ready",
  "is-revealed",
  "is-reveal-complete",
];

function clearReveal(element) {
  const finishReveal = revealFinishHandlers.get(element);
  if (finishReveal) {
    element.removeEventListener("transitionend", finishReveal);
    revealFinishHandlers.delete(element);
  }

  revealObserver?.unobserve(element);
  element.classList.remove(...revealClasses);
}

function observeReveal(element) {
  if (!window.IntersectionObserver) {
    element.classList.add("is-revealed", "is-reveal-complete");
    return;
  }

  revealObserver ??= new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        const target = entry.target;
        const finishReveal = (event) => {
          if (event.propertyName !== "opacity") return;
          target.classList.add("is-reveal-complete");
          target.removeEventListener("transitionend", finishReveal);
          revealFinishHandlers.delete(target);
        };

        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
          target.classList.add("is-revealed", "is-reveal-complete");
          observer.unobserve(target);
          return;
        }

        revealFinishHandlers.set(target, finishReveal);
        target.addEventListener("transitionend", finishReveal);
        target.classList.add("is-revealed");
        observer.unobserve(target);
      });
    },
    { threshold: 0.15 },
  );

  element.classList.add("is-reveal-ready");
  revealObserver.observe(element);
}

/**
 * Scan the DOM for .ds-scope elements and mount the corresponding
 * Vue component into each one.  Sitecore (or dev.html) provides:
 *   - data-component  — key into the `components` registry
 *   - data-props      — JSON-encoded props object (optional)
 */
function mountAll() {
  // Activate the institutional theme for the design system.
  // The DS reads this attribute from <body> for CSS variable scoping and
  // some components also check it via JS (e.g. ContentItemInfo).
  document.body.setAttribute("data-theme", "institutional");

  document.querySelectorAll(".ds-scope[data-component]").forEach((el) => {
    // Skip elements that were already mounted
    if (instances.has(el)) return;

    const name = el.dataset.component;

    let props = {};
    try {
      props = JSON.parse(el.dataset.props || "{}");
    } catch (err) {
      console.error(
        `[VueWidgets] Invalid JSON in data-props for "${name}":`,
        err,
      );
      return;
    }

    const component = components[name];

    if (!component) {
      console.warn(`[VueWidgets] Component not found: "${name}"`);
      return;
    }

    const shouldReveal = !revealExcludedComponents.has(name);
    if (shouldReveal) el.classList.add("ca-mount-reveal");

    const app = createApp(component, props);
    // Makes `v-skeleton` available to every component.
    app.directive("skeleton", skeleton);
    app.mount(el);
    instances.set(el, app);

    if (shouldReveal) observeReveal(el);
  });
}

/**
 * Unmount all Vue instances previously created by mountAll().
 * Useful when the host page does partial DOM replacements.
 */
function unmountAll() {
  revealObserver?.disconnect();
  revealObserver = undefined;
  instances.forEach((app, element) => {
    clearReveal(element);
    app.unmount();
  });
  instances.clear();
}

export { mountAll, unmountAll };
